import { Button, DatePicker, Input, Select, SelectChangeEvent, useFlashMessage } from '@indigo-ag/component-module';
import React, { useContext, useEffect, useState } from 'react';

import { Checkbox, CollapsibleContentCard, Textarea } from '@indigo-ag/component-module';

import { useMutation, useQuery } from '@apollo/client';
import {
  AgFinancingEvaluationType,
  AgFinancingMatricula,
  AgFinancingMatriculaDetail,
  AgFinancingMatriculaDetailInput,
  AgMatriculaDetailType,
  FinancingMunicipality,
  FinancingState,
  MatriculaBiome,
  Phase2Input
} from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { MaterialUiPickersDate } from 'material-ui-pickers/typings/date';

import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  financingAgFinancing,
  financingMunicipalitiesByState,
  financingStates,
  updateAgFinancingMatricula
} from 'features/brazil-grower/queries';
import { GridContainer, biomeTypesOptions, evaluationTypesOptions, usePrompt } from 'shared';
import { ConfirmationDialog } from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import styled from 'styled-components';
import { CollateralAnalysisContext } from '../../CollateralAnalysis/CollateralAnalysis/CollateraAnalysis';
import { CollateralFormalizationContext } from '../../CollateralAnalysis/CollateralFormalization/CollateralFormalization';
import { MatriculaAnalysisSummary } from './MatriculaAnalysisSummary';
import { getDetail } from './utils';

interface MatriculaAnalysisProps {
  matriculaData: AgFinancingMatricula;
  readonly?: boolean;
  includedInFormalization?: boolean;
}

const StyledCollapsibleContentCard = styled(CollapsibleContentCard)`
  margin-bottom: 16px;
`;

export const MatriculaAnalysis: React.FunctionComponent<MatriculaAnalysisProps> = ({
  matriculaData,
  readonly = false,
  includedInFormalization = false
}) => {
  const matriculaTypeLegal = matriculaData.details?.filter(d => !!d.id);
  const agFinancingMatriculaDetail: Partial<AgFinancingMatriculaDetail> = {};
  if (!!matriculaTypeLegal?.length) {
    if (Object.keys(matriculaTypeLegal[0]).length !== 0)
      for (let detail in matriculaTypeLegal[0]) {
        agFinancingMatriculaDetail[detail] = matriculaTypeLegal[0][detail];
      }
  }

  const initialState: Partial<AgFinancingMatriculaDetailInput> = {
    emissionDate: agFinancingMatriculaDetail.emissionDate || undefined,
    owners: agFinancingMatriculaDetail.owners || '',
    farmName: agFinancingMatriculaDetail.farmName || '',
    registrationNumbers: agFinancingMatriculaDetail.registrationNumbers || '',
    totalArea: agFinancingMatriculaDetail.totalArea || '',
    cultivableArea: agFinancingMatriculaDetail.cultivableArea || 0,
    comments: agFinancingMatriculaDetail.comments,
    latitude: agFinancingMatriculaDetail.latitude,
    longitude: agFinancingMatriculaDetail.longitude,
    preservationArea: agFinancingMatriculaDetail.preservationArea,
    otherArea: agFinancingMatriculaDetail.otherArea,
    complianceConflictArea: agFinancingMatriculaDetail.complianceConflictArea,
    ibamaEmbargo: agFinancingMatriculaDetail.ibamaEmbargo,
    conservationUnities: agFinancingMatriculaDetail.conservationUnities,
    indigenousLand: agFinancingMatriculaDetail.indigenousLand,
    ICMBIOEmbargo: agFinancingMatriculaDetail.ICMBIOEmbargo,
    stateEmbargo: agFinancingMatriculaDetail.stateEmbargo,
    quilimbolasLand: agFinancingMatriculaDetail.quilimbolasLand,
    biome: agFinancingMatriculaDetail.biome,
    legalComments: agFinancingMatriculaDetail.legalComments,
    deforestationAfter2008: agFinancingMatriculaDetail.deforestationAfter2008,
    permanentPreservationArea: agFinancingMatriculaDetail.permanentPreservationArea
  };
  /**
   * Context
   */
  const { updatePhase2 } = useContext(CollateralAnalysisContext);
  const { updatePhase2Formalization } = useContext(CollateralFormalizationContext);
  const { agFinancingId } = useContext(FinanceProcessContext);

  /**
   * State
   */
  const [state, setState] = useState({ ...initialState });
  const [selectedState, setSelectedState] = useState(agFinancingMatriculaDetail.state?.id || '0');
  const [selectedMunicipality, setSelectedMunicipality] = useState(agFinancingMatriculaDetail.municipality?.id || '0');
  const [removeAnalysisOpen, setRemoveAnalysisOpen] = useState(false);
  const [selectedMatricula, setSelectedMatricula] = useState<AgFinancingMatricula>();
  const [busy, setBusy] = useState(false);
  const [hasDetail, setHastDetail] = useState(!!matriculaTypeLegal?.length);
  const [isUpdatingInformation, setIsUpdatingInformation] = useState(false);

  const [selectedBiome, setSelectedBiome] = useState<MatriculaBiome | null>(initialState.biome || null);

  /**
   * Queries
   */
  const { data: statesData } = useQuery<{ financingStates: FinancingState[] }>(financingStates);
  const { data: municipalitiesData } = useQuery<{ financingMunicipalities: FinancingMunicipality[] }>(
    financingMunicipalitiesByState,
    {
      variables: { stateId: Number(selectedState) }
    }
  );

  /**
   * Mutations
   */
  const [updateAgFinancingMatriculaFn] = useMutation(updateAgFinancingMatricula);

  const { addFlashMessage } = useFlashMessage();

  useEffect(() => {
    setState({ ...initialState });
    setSelectedState(agFinancingMatriculaDetail.state?.id || '0');
    setSelectedMunicipality(agFinancingMatriculaDetail.municipality?.id || '0');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | SelectChangeEvent<AgFinancingEvaluationType>
      | React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setState({ ...state, [event.target.name]: event.target.value });
    setIsUpdatingInformation(true);
  };

  const handleStateChange = (event: SelectChangeEvent<string>): void => {
    setSelectedState(event.target.value || '0');
    setIsUpdatingInformation(true);
  };

  const handleMunicipalityChange = (event: SelectChangeEvent<string>): void => {
    setSelectedMunicipality(event.target.value || '0');
    setIsUpdatingInformation(true);
  };

  const handleDateChange = (date: MaterialUiPickersDate): void => {
    setState({ ...state, emissionDate: date._d });
    setIsUpdatingInformation(true);
  };

  const handleRemoveMatricula = (matricula: AgFinancingMatricula) => {
    return () => {
      setSelectedMatricula(matricula);
      setRemoveAnalysisOpen(true);
    };
  };

  const closeRemoveModal = () => {
    setRemoveAnalysisOpen(false);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.name === 'preservationAreaNA') {
      if (state.preservationArea === undefined || state.preservationArea === null) {
        setState({ ...state, preservationArea: '' });
      } else {
        setState({ ...state, preservationArea: undefined });
      }
    } else if (event.target.name === 'complianceConflictAreaNA') {
      if (!!state.complianceConflictArea === undefined || state.complianceConflictArea === null) {
        setState({ ...state, complianceConflictArea: '' });
      } else {
        setState({ ...state, complianceConflictArea: undefined });
      }
    }
  };

  const handleBiomeChange = (event: SelectChangeEvent<MatriculaBiome>): void => {
    setSelectedBiome(event.target.value);
  };

  const handleUpdatePhase2 = () => {
    const matriculaInfo: Partial<Phase2Input> = {
      matriculas: [
        {
          id: matriculaData.id,
          detail: {
            ...state,
            cultivableArea: Number(state.cultivableArea),
            state: Number(selectedState) > 0 ? { id: Number(selectedState) } : undefined,
            municipality: Number(selectedMunicipality) > 0 ? { id: Number(selectedMunicipality) } : undefined,
            detailType: AgMatriculaDetailType.LEGAL,
            biome: selectedBiome as MatriculaBiome,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            preservationArea: state.preservationArea === undefined ? null : state.preservationArea,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            complianceConflictArea: state.complianceConflictArea === undefined ? null : state.complianceConflictArea
          }
        }
      ]
    };

    return includedInFormalization ? updatePhase2Formalization(matriculaInfo) : updatePhase2(matriculaInfo);
  };

  const updateInfo = () => {
    setBusy(true);
    return handleUpdatePhase2()
      .then(() => {
        setHastDetail(true);
        addFlashMessage({
          content: `${i18next.t('matriculas')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
          title: '',
          variant: 'success'
        });
        setIsUpdatingInformation(false);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const removeMatriculaPromise = () => {
    return updateAgFinancingMatriculaFn({
      refetchQueries: [{ query: financingAgFinancing, variables: { agFinancingId } }],
      variables: {
        matricula: {
          id: selectedMatricula?.id,
          detail: {
            detailType: AgMatriculaDetailType.LEGAL,
            remove: true
          }
        },
        agFinancingId
      }
    }).then(() => {
      setHastDetail(false);
      setIsUpdatingInformation(false);
      addFlashMessage({
        content: `${i18next.t('matricula')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
        title: '',
        variant: 'success'
      });
      setState({
        emissionDate: undefined,
        owners: '',
        farmName: '',
        registrationNumbers: '',
        totalArea: '',
        cultivableArea: 0
      });
    });
  };

  usePrompt(isUpdatingInformation);

  const canSaveMatricula = !state.registrationNumbers || (includedInFormalization && !state.cultivableArea);

  return readonly ? (
    <MatriculaAnalysisSummary matriculaData={matriculaData} />
  ) : (
    <>
      <StyledCollapsibleContentCard titleText={i18next.t('dataProperty')}>
        <GridContainer columns={2}>
          <DatePicker
            name="emissionDate"
            label={i18next.t('emissionDate')}
            value={state.emissionDate || null}
            sizeVariant="slim"
            onChange={handleDateChange}
          />
          <Input
            label={`${i18next.t('registrationNumber')}*`}
            name="registrationNumbers"
            onChange={handleChange}
            value={state.registrationNumbers || ''}
            sizeVariant="slim"
          />
          <Select
            label={i18next.t('state')}
            value={`${selectedState}`}
            name="stateId"
            options={statesData ? statesData.financingStates.map(s => ({ name: s.name, value: s.id })) : []}
            onChange={handleStateChange}
            sizeVariant="slim"
          />
          <Select
            label={i18next.t('municipality')}
            value={`${selectedMunicipality}`}
            name="municipalityId"
            options={
              Number(selectedState) > 0 && municipalitiesData
                ? municipalitiesData.financingMunicipalities.map(m => ({
                    name: m.name,
                    value: m.id
                  }))
                : []
            }
            onChange={handleMunicipalityChange}
            sizeVariant="slim"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>

      <StyledCollapsibleContentCard titleText={i18next.t('area')}>
        <GridContainer columns={2}>
          <Input
            label={`${i18next.t('totalArea')}*`}
            name="totalArea"
            onChange={handleChange}
            value={!state.totalArea || isNaN(Number(state.totalArea)) ? '' : String(state.totalArea)}
            sizeVariant="slim"
          />
          <Input
            label={`${i18next.t('cultivableArea')}*`}
            name="cultivableArea"
            onChange={handleChange}
            value={!state.cultivableArea || isNaN(Number(state.cultivableArea)) ? '' : String(state.cultivableArea)}
            sizeVariant="slim"
          />

          <Select
            label={`${i18next.t('biome')}*`}
            name="biome"
            options={biomeTypesOptions}
            value={selectedBiome}
            onChange={handleBiomeChange}
            sizeVariant="slim"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>

      <StyledCollapsibleContentCard titleText={i18next.t('restrictions')}>
        <GridContainer columns={2}>
          <div>
            <Input
              label={`${i18next.t('preservationArea')}*`}
              name="preservationArea"
              onChange={handleChange}
              value={
                !state.preservationArea || isNaN(Number(state.preservationArea)) ? '' : String(state.preservationArea)
              }
              sizeVariant="slim"
            />
            <Checkbox
              name="preservationAreaNA"
              checked={state.preservationArea === null || state.preservationArea === undefined}
              label={'N/A'}
              onChange={handleCheckboxChange}
              margin="dense"
            />
          </div>

          <div>
            <Input
              label={i18next.t('complianceConflictArea')}
              name="complianceConflictArea"
              onChange={handleChange}
              value={
                !state.complianceConflictArea || isNaN(Number(state.complianceConflictArea))
                  ? ''
                  : String(state.complianceConflictArea)
              }
              sizeVariant="slim"
            />
            <Checkbox
              name="complianceConflictAreaNA"
              checked={state.complianceConflictArea === null || state.complianceConflictArea === undefined}
              label={'N/A'}
              onChange={handleCheckboxChange}
              margin="dense"
            />
          </div>

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('ibamaEmbargo')}
            name="ibamaEmbargo"
            options={evaluationTypesOptions}
            value={state.ibamaEmbargo}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('conservationUnities')}
            name="conservationUnities"
            options={evaluationTypesOptions}
            value={state.conservationUnities}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('indigenousLand')}
            name="indigenousLand"
            options={evaluationTypesOptions}
            value={state.indigenousLand}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('quilimbolasLand')}
            name="quilimbolasLand"
            options={evaluationTypesOptions}
            value={state.quilimbolasLand}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('ICMBIOEmbargo')}
            name="ICMBIOEmbargo"
            options={evaluationTypesOptions}
            value={state.ICMBIOEmbargo}
            onChange={handleChange}
            sizeVariant="slim"
          />
          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('stateEmbargo')}
            name="stateEmbargo"
            options={evaluationTypesOptions}
            value={state.stateEmbargo}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('permanentPreservationArea')}
            name="permanentPreservationArea"
            options={evaluationTypesOptions}
            value={state.permanentPreservationArea}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('deforestationAfter2008')}
            name="deforestationAfter2008"
            options={evaluationTypesOptions}
            value={state.deforestationAfter2008}
            onChange={handleChange}
            sizeVariant="slim"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>

      <StyledCollapsibleContentCard titleText={i18next.t('evaluation')}>
        <GridContainer columns={2}>
          <Textarea
            label={i18next.t('legalComments')}
            name="legalComments"
            onChange={handleChange}
            value={!state.legalComments ? '' : String(state.legalComments)}
            sizeVariant="regular"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {hasDetail
          ? !!getDetail(true, matriculaData) && (
              <Button
                type="submit"
                size="small"
                variant="secondary"
                destructive
                onClick={handleRemoveMatricula(matriculaData)}
              >
                {i18next.t('removeMatriculaAnalysis')}
              </Button>
            )
          : null}
        <Button onClick={updateInfo} type="submit" size="small" busy={busy} disabled={canSaveMatricula}>
          {i18next.t('save')}
        </Button>
      </div>
      <ConfirmationDialog
        open={removeAnalysisOpen}
        closeDialog={closeRemoveModal}
        confirmationText="removeAnalysis"
        confirmationButtonText="remove"
        onConfirmPromise={removeMatriculaPromise}
        destructive
      />
    </>
  );
};
